import { useState } from 'react';
import { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword, ResetPasswordBodySchema } from '../lib/api/auth.gen';
import { Form, FormError, FormField, FormSubmit, Input, Stack, useZodForm } from '../components/ui';

// G-26 (story 2-95b): composed over the generated contract schema, per the
// story's own worked example — the wire body is just `password`; `confirm`
// is a UI-only field the wire contract has no business carrying, same
// reasoning as AcceptInvitePage's `.extend()`/`.refine()`. The reset TOKEN is
// not a form field at all (it comes from the URL query string, supplied by
// the page, not typed by the user), so it never enters this schema — unlike
// AcceptInvitePage's `token`, which the generated `AcceptInviteBodySchema`
// does carry and has to `.omit()`.
const resetPasswordFormSchema = ResetPasswordBodySchema.extend({ confirm: z.string().min(8) }).refine(
  (values) => values.password === values.confirm,
  { message: 'Passwords do not match.', path: ['confirm'] },
);

export function ResetPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'idle' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);
  const form = useZodForm(resetPasswordFormSchema, { defaultValues: { password: '', confirm: '' } });

  const handleSubmit = async (values: z.input<typeof resetPasswordFormSchema>) => {
    setError(null);

    if (!token) {
      setError('Reset token missing. Please request a new reset link.');
      return;
    }

    try {
      await resetPassword({ password: values.password }, { token });
      setStatus('success');
    } catch (err) {
      setError('Unable to reset password. Please request a new link.');
    }
  };

  return (
    // Same deliberate `max-w-md` exception LoginPage.tsx's own header comment
    // documents — this page also renders inside PublicLayout's chrome.
    <Stack gap={8} className="mx-auto max-w-md">
      <Stack gap={3} className="text-center">
        <h1 className="text-3xl font-semibold text-foreground">Choose a new password</h1>
        <p className="text-sm text-muted-foreground">
          Set a fresh password for your DefectSnap account.
        </p>
      </Stack>

      <Form form={form} onSubmit={handleSubmit} className="rounded-hairline border border-border bg-card space-y-4 p-6">
        <FormField name="password" label="New password" required>
          {(field) => <Input type="password" className="w-full" placeholder="Create a strong password" {...field} />}
        </FormField>

        <FormField name="confirm" label="Confirm password" required>
          {(field) => <Input type="password" className="w-full" placeholder="Repeat your new password" {...field} />}
        </FormField>

        <FormError message={error} />
        {status === 'success' && (
          <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
            Password updated. You can now log in.
          </p>
        )}

        <FormSubmit className="w-full" disabled={status === 'success'} pendingLabel="Updating…">
          {status === 'success' ? 'Updated' : 'Update password'}
        </FormSubmit>
      </Form>

      <p className="text-center text-sm text-muted-foreground">
        Back to{' '}
        <Link to="/login" className="text-primary hover:underline">
          log in
        </Link>
        .
      </p>
    </Stack>
  );
}
